/**
 * Datadog RUM configuration loader
 * Validates and loads configuration from environment variables
 */

import type { DatadogConfig } from "./types";
import { logger } from "./logger";

/**
 * Load Datadog configuration from environment variables
 */
export function loadDatadogConfig(): DatadogConfig | null {
  // Disable Datadog for local development only (when running npm run dev)
  // Check if running locally by looking for dev server flag
  const isLocalDev = import.meta.env.DEV;
  if (isLocalDev) {
    logger.info("RUM disabled for local development");
    return null;
  }

  const applicationId = import.meta.env.VITE_DATADOG_APPLICATION_ID || "your-app-id-here";
  const clientToken = import.meta.env.VITE_DATADOG_CLIENT_TOKEN || "your-token-here";

  // Required credentials check
  if (!applicationId || !clientToken) {
    const env = import.meta.env.VITE_DATADOG_ENV || import.meta.env.MODE;
    const message =
      "Missing required credentials (VITE_DATADOG_APPLICATION_ID or VITE_DATADOG_CLIENT_TOKEN). RUM will not be initialized.";

    if (env === "production") {
      logger.error(message);
    } else {
      logger.warn(message);
    }

    return null;
  }

  // Check if explicitly disabled
  const enabled = import.meta.env.VITE_DATADOG_ENABLED;
  if (enabled === "false") {
    logger.info("RUM is explicitly disabled via VITE_DATADOG_ENABLED");
    return null;
  }

  // Determine environment
  const environment = import.meta.env.VITE_DATADOG_ENV || import.meta.env.MODE || "production";

  // Get environment-specific sampling rates
  const samplingRates = getSamplingRatesForEnvironment(environment);

  // Build configuration with defaults
  const config: DatadogConfig = {
    applicationId,
    clientToken,
    site: import.meta.env.VITE_DATADOG_SITE || "datadoghq.com",
    service: import.meta.env.VITE_DATADOG_SERVICE || "pax-frontend",
    env: environment,
    version: import.meta.env.VITE_DATADOG_VERSION || "0.0.0",
    sessionSampleRate: parseNumber(
      import.meta.env.VITE_DATADOG_SESSION_SAMPLE_RATE,
      samplingRates.sessionSampleRate
    ),
    sessionReplaySampleRate: parseNumber(
      import.meta.env.VITE_DATADOG_SESSION_REPLAY_SAMPLE_RATE,
      samplingRates.sessionReplaySampleRate
    ),
    trackUserInteractions: parseBoolean(import.meta.env.VITE_DATADOG_TRACK_USER_INTERACTIONS, true),
    trackResources: parseBoolean(import.meta.env.VITE_DATADOG_TRACK_RESOURCES, true),
    trackLongTasks: parseBoolean(import.meta.env.VITE_DATADOG_TRACK_LONG_TASKS, true),
  };

  return config;
}

/**
 * Get sampling rates based on environment
 */
function getSamplingRatesForEnvironment(environment: string): {
  sessionSampleRate: number;
  sessionReplaySampleRate: number;
} {
  switch (environment) {
    case "production":
      return {
        sessionSampleRate: 100, // Track 100% of sessions
        sessionReplaySampleRate: 20, // Record 20% of sessions
      };
    case "development":
      return {
        sessionSampleRate: 100, // Track 100% of sessions
        sessionReplaySampleRate: 0, // Record 100% of sessions (full visibility for testing)
      };
    case "staging":
      return {
        sessionSampleRate: 100, // Track 100% of sessions
        sessionReplaySampleRate: 10, // Record 50% of sessions
      };
    default:
      // Fallback for any other environment
      return {
        sessionSampleRate: 100,
        sessionReplaySampleRate: 20,
      };
  }
}

/**
 * Parse a string value as a number with fallback
 */
function parseNumber(value: unknown, defaultValue: number): number {
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const parsed = Number(value);
    if (!isNaN(parsed)) return parsed;
  }
  return defaultValue;
}

/**
 * Parse a string value as a boolean with fallback
 */
function parseBoolean(value: unknown, defaultValue: boolean): boolean {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    const lower = value.toLowerCase();
    if (lower === "true" || lower === "1") return true;
    if (lower === "false" || lower === "0") return false;
  }
  return defaultValue;
}

/**
 * Get the current Datadog configuration (for debugging)
 */
export function getDatadogConfig(): DatadogConfig | null {
  return loadDatadogConfig();
}
